<template>

	<a-card :bordered="false" class="dashboard-line-chart">
		<div class="card-header">
			<div class="header-left">
				<h6 class="card-title">产量总览</h6>
				<div class="card-comparison">
					<template v-if="loading">加载中...</template>
					<template v-else-if="yearOverYearPercentage > 0">较去年<span class="text-success">+{{ yearOverYearPercentage }}%</span></template>
					<template v-else-if="yearOverYearPercentage < 0">较去年<span class="text-danger">{{ yearOverYearPercentage }}%</span></template>
					<template v-else>较去年<span class="text-neutral">0%</span></template>
				</div>
			</div>
			<div class="header-right">
				<span class="legend-item">
					<span class="legend-dot legend-current"></span>
					<span class="legend-text">{{ currentYear }}</span>
				</span>
				<span class="legend-item">
					<span class="legend-dot legend-previous"></span>
					<span class="legend-text">{{ previousYear }}</span>
				</span>
			</div>
		</div>
		<div v-if="loading" class="chart-loading">
			<a-spin tip="加载中..." />
		</div>
		<div v-else class="chart-container">
			<canvas ref="lineChart"></canvas>
		</div>
	</a-card>

</template>

<script>
	import { Chart, registerables } from 'chart.js';
	Chart.register(...registerables);

	export default ({
		data() {
			return {
				loading: true,
				yearOverYearPercentage: 0,
				currentYear: new Date().getFullYear(),
				previousYear: new Date().getFullYear() - 1,
				chart: null,
				chartData: null,
			}
		},
		async mounted() {
			await this.loadData();
		},
		beforeDestroy() {
			if (this.chart) {
				this.chart.destroy();
			}
		},
		methods: {
			async loadData() {
				try {
					const res = await this.$api.get_production_overview();
					if (res && res.code === 200 && res.data) {
						const { current_year, previous_year, year_over_year_percentage } = res.data;
						this.currentYear = current_year.year;
						this.previousYear = previous_year.year;
						this.yearOverYearPercentage = year_over_year_percentage;
						// 转换月份为中文格式（01 -> 1月）
						const formatMonth = (month) => {
							const monthNum = parseInt(month);
							return `${monthNum}月`;
						};
						this.chartData = {
							labels: current_year.monthly_data.map(d => formatMonth(d.month)),
							currentData: current_year.monthly_data.map(d => d.count),
							previousData: previous_year.monthly_data.map(d => d.count),
						};
					}
				} catch (e) {
					console.error('[CardLineChart] 加载产量总览数据失败', e);
				} finally {
					this.loading = false;
					// Wait for DOM update then init chart
					this.$nextTick(() => {
						this.initChart();
					});
				}
			},
			initChart() {
				if (!this.$refs.lineChart) return;
				const ctx = this.$refs.lineChart.getContext('2d');
				const labels = this.chartData ? this.chartData.labels : [];
				const currentData = this.chartData ? this.chartData.currentData : [];
				const previousData = this.chartData ? this.chartData.previousData : [];

				this.chart = new Chart(ctx, {
					type: 'line',
					data: {
						labels,
						datasets: [
							{
								label: String(this.currentYear),
								tension: 0.4,
								borderWidth: 2,
								pointRadius: 0,
								borderColor: '#2563EB',
								backgroundColor: 'transparent',
								data: currentData,
								maxBarThickness: 6,
							},
							{
								label: String(this.previousYear),
								tension: 0.4,
								borderWidth: 2,
								pointRadius: 0,
								borderColor: '#93C5FD',
								backgroundColor: 'transparent',
								data: previousData,
								maxBarThickness: 6,
							},
						],
					},
					options: {
						layout: {
							padding: { top: 10, right: 10, left: 10, bottom: 5 },
						},
						responsive: true,
						maintainAspectRatio: false,
						plugins: {
							legend: { display: false },
							tooltip: {
								callbacks: {
									label: function(context) {
										const value = context.parsed.y;
										const formattedValue = value !== null ? value.toFixed(2) : '0';
										return `${context.dataset.label}: ${formattedValue} kg`;
									}
								}
							},
						},
						scales: {
							y: {
								grid: {
									display: true,
									color: 'rgba(0, 0, 0, 0.05)',
								},
								ticks: {
									suggestedMin: 0,
									display: true,
									color: '#6B7280',
									font: { size: 12 },
									callback: function(value) {
										return value.toFixed(1) + ' kg';
									}
								},
							},
							x: {
								grid: { display: false },
								ticks: {
									display: true,
									color: '#6B7280',
									font: { size: 12 },
								},
							},
						},
					},
				});
			},
		},
	})

</script>

<style lang="scss" scoped>
.dashboard-line-chart {
	border-radius: 16px;
	border: 1px solid #E5E7EB;
	box-shadow: rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.1) 0px 1px 2px -1px;
	background: #FFFFFF;
	height: 100%;
	display: flex;
	flex-direction: column;

	.card-header {
		display: flex;
		justify-content: space-between;
		align-items: flex-start;
		margin-bottom: 16px;
		flex-shrink: 0;
	}

	.header-left {
		display: flex;
		flex-direction: column;
		gap: 4px;
	}

	.card-title {
		font-size: 16px;
		font-weight: 600;
		color: #111827;
		margin: 0;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
	}

	.card-comparison {
		font-size: 13px;
		color: #6B7280;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

		.text-success {
			color: #10B981;
			font-weight: 500;
			margin-left: 4px;
		}

		.text-danger {
			color: #EF4444;
			font-weight: 500;
			margin-left: 4px;
		}

		.text-neutral {
			color: #6B7280;
			margin-left: 4px;
		}
	}

	.header-right {
		display: flex;
		gap: 16px;
	}

	.legend-item {
		display: flex;
		align-items: center;
		gap: 6px;
	}

	.legend-dot {
		width: 10px;
		height: 10px;
		border-radius: 50%;

		&.legend-current {
			background-color: #2563EB;
		}

		&.legend-previous {
			background-color: #93C5FD;
		}
	}

	.legend-text {
		font-size: 12px;
		color: #6B7280;
		font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
	}

	.chart-loading {
		height: 300px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex: 1;
	}

	.chart-container {
		height: 300px;
		flex: 1;
		position: relative;
	}

	::v-deep .ant-card-body {
		padding: 24px;
		display: flex;
		flex-direction: column;
		flex: 1;
	}
}
</style>
